import React, { useState, useEffect } from 'react';
import { getAllDecisionMakers, createDecisionMaker, updateDecisionMaker, deleteDecisionMaker, uploadImage } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Pencil, Trash2, Upload, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';

const AdminDecisionMakers = () => {
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // ID of item pending delete confirmation
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    category: '',
    image_url: '',
    order: 0,
    active: true
  });

  const categories = ['Cybersecurity', 'Developer Tools', 'Finance', 'HR Tech', 'Infrastructure', 'Other'];

  useEffect(() => {
    loadDecisionMakers();
  }, []);

  const loadDecisionMakers = async () => {
    try {
      const data = await getAllDecisionMakers();
      setDecisionMakers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading decision makers:', error);
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImage(file);
      setFormData({ ...formData, image_url: result.url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateDecisionMaker(editing, formData);
      } else {
        await createDecisionMaker(formData);
      }
      loadDecisionMakers();
      resetForm();
    } catch (error) {
      console.error('Error saving decision maker:', error);
      alert('Error saving decision maker');
    }
  };

  const handleEdit = (dm) => {
    setEditing(dm._id);
    setFormData({
      name: dm.name,
      title: dm.title,
      company: dm.company,
      category: dm.category,
      image_url: dm.image_url || '',
      order: dm.order,
      active: dm.active
    });
    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    // First click shows confirm button, second click deletes
    if (confirmDelete === id) {
      performDelete(id);
    } else {
      setConfirmDelete(id);
      // Auto-reset after 3 seconds if not confirmed
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const performDelete = async (id) => {
    setDeleting(id);
    setConfirmDelete(null);
    try {
      await deleteDecisionMaker(id);
      // Remove from local state immediately for instant feedback
      setDecisionMakers(prev => prev.filter(dm => dm._id !== id));
    } catch (error) {
      console.error('Error deleting decision maker:', error);
      alert('Error deleting. Please log out and log back in.');
      loadDecisionMakers();
    } finally {
      setDeleting(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      company: '',
      category: '',
      image_url: '',
      order: 0,
      active: true
    });
    setEditing(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">Loading...</div>;
  }

  return (
    <AdminLayout currentPage="decision-makers">
      {/* Header */}
      <div className="mb-6">
        <p className="text-slate-400 text-sm">
          Manage the "Decision Makers from Previous Events" section on the homepage. 
          These are buyers/judges who have participated in past pitch events.
        </p>
      </div>

      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-decision-maker-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Decision Maker
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-8 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">
              {editing ? 'Edit Decision Maker' : 'Add New Decision Maker'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Photo
                </label>
                <div className="flex items-start gap-4">
                  {formData.image_url ? (
                    <div className="relative">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-20 h-20 rounded-full object-cover ring-2 ring-slate-700"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center text-slate-500 ring-2 ring-dashed ring-slate-700">
                      <Upload className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 inline-flex items-center gap-2 transition-colors">
                        <Upload className="w-4 h-4" />
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                    </label>
                    <p className="text-xs text-slate-500 mt-2">
                      Or paste image URL directly below
                    </p>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full mt-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="image-url-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., John Smith"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., CISO, VP of Engineering"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="title-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="e.g., Brex, GitHub, Okta"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="company-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Category *
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="category-select"
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="order-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Lower numbers appear first</p>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 rounded"
                      data-testid="active-checkbox"
                    />
                    Active (visible on homepage)
                  </label>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-btn"
                >
                  {editing ? 'Update' : 'Create'} Decision Maker
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm} 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="grid gap-4">
        {decisionMakers.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center">
              <p className="text-slate-400">No decision makers yet. Add your first one!</p>
            </CardContent>
          </Card>
        ) : (
          decisionMakers.map((dm) => (
            <Card 
              key={dm._id} 
              className={`bg-slate-900 border-slate-800 ${!dm.active ? 'opacity-60' : ''}`}
              data-testid={`decision-maker-card-${dm._id}`}
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    {dm.image_url ? (
                      <img 
                        src={dm.image_url} 
                        alt={dm.name}
                        className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-700"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {dm.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-white">{dm.name}</h3>
                        {!dm.active && (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/30">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-blue-400 font-medium text-sm">{dm.title}</p>
                      <p className="text-slate-400 text-sm">{dm.company}</p>
                      <Badge className="mt-1 bg-slate-800 text-slate-300 border-slate-700 text-xs">
                        {dm.category}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(dm)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid={`edit-btn-${dm._id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(dm._id)}
                      disabled={deleting === dm._id}
                      className={`${
                        confirmDelete === dm._id 
                          ? 'bg-red-600 border-red-600 text-white hover:bg-red-700' 
                          : 'border-red-800 text-red-400 hover:bg-red-900/20'
                      } disabled:opacity-50 min-w-[80px]`}
                      data-testid={`delete-btn-${dm._id}`}
                    >
                      {deleting === dm._id ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : confirmDelete === dm._id ? (
                        <span className="text-xs font-medium">Confirm?</span>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDecisionMakers;
